import type { FC } from 'react';
import { useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';

import { useGetAvailableTokens } from '@features/investigations/components/modals/add-new-investigation/hooks/useGetAvailableTokens';
import { useTokenReservation } from '@features/investigations/components/modals/add-new-investigation/hooks/useTokenReservation';
import type { NewInvestigationFormValues } from '@features/investigations/components/modals/add-new-investigation/investigation.schema';
import { getNewInvestigationFormOptions } from '@features/investigations/components/modals/add-new-investigation/investigation.schema';
import { NewInvestigationForm } from '@features/investigations/components/modals/add-new-investigation/NewInvestigation.form';
import { TokenAvailability } from '@features/investigations/components/modals/add-new-investigation/TokenAvailability';
import { TokenEstimation } from '@features/investigations/components/modals/add-new-investigation/TokenEstimation';
import { GenericModal } from '@shared/components/ui/custom';

export interface NewInvestigationProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const AddNewInvestigationModal: FC<NewInvestigationProps> = ({ onClose, onSuccess }) => {
  const {
    availableTokens,
    lastUpdated,
    handleRefresh,
    isLoadingTokens,
    isError,
    errorMessage: availabilityErrorMessage,
  } = useGetAvailableTokens();

  const form = useForm<NewInvestigationFormValues>(getNewInvestigationFormOptions());

  const watchedDateRange = useWatch({ control: form.control, name: 'dateRange' });
  const watchedAssets = useWatch({ control: form.control, name: 'assets' });

  const {
    estimatedTokens,
    isEstimating,
    hasError: estimationHasError,
    errorMessage: estimationErrorMessage,
    markAsLaunched,
    resetAllMutations,
    reservationId,
    retryEstimate,
  } = useTokenReservation({
    dateRange: watchedDateRange,
    assets: watchedAssets,
  });

  const hasReachedLimit = availableTokens !== null && estimatedTokens !== null && availableTokens < estimatedTokens;

  useEffect(() => {
    form.setValue('reservationId', reservationId ?? '', {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [form, reservationId]);

  return (
    <GenericModal
      open
      title="New Investigation"
      data-testid="add-investigation-modal"
      contentProps={{ className: 'max-w-4xl w-full' }}
      onOpenChange={(open) => !open && onClose()}
    >
      <div className="flex flex-col gap-1 mt-1.5" data-testid="add-investigation-modal-token-summary">
        <TokenAvailability
          availableTokens={availableTokens}
          lastUpdated={lastUpdated}
          isLoading={isLoadingTokens}
          isError={isError}
          errorMessage={availabilityErrorMessage}
          handleRefresh={handleRefresh}
        />
        <TokenEstimation
          tokens={estimatedTokens}
          showSkeleton={isEstimating}
          hasError={estimationHasError}
          errorMessage={estimationErrorMessage}
          onRetry={retryEstimate}
        />
      </div>
      <NewInvestigationForm
        form={form}
        onClose={onClose}
        onSuccess={onSuccess}
        hasReachedLimit={hasReachedLimit}
        isEstimating={isEstimating}
        estimationHasError={estimationHasError}
        markAsLaunched={markAsLaunched}
        resetAllMutations={resetAllMutations}
      />
    </GenericModal>
  );
};
